import { faArrowLeft, faArrowRight } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import { Link, usePage } from '@inertiajs/react';

import { Icon } from '@/components/ui/icon';
import {
  Pagination,
  PaginationButton,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
} from '@/components/ui/pagination';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import { cn } from '@/lib/utils';
import type { PaginationInfo } from '@/types/pagination';

interface IndexPaginationProps {
  pagination: PaginationInfo;
  className?: string;
  fullWidth?: boolean;
}

/**
 * Pagination for index pages (blog, case results, reviews).
 * Uses Inertia links to preserve query params while changing page.
 */
export function IndexPagination({
  pagination,
  className,
  fullWidth = false,
}: IndexPaginationProps) {
  const { url } = usePage();
  const isPageEditor = useIsPageEditor();
  const { currentPage, totalPages } = pagination;

  const pageHref = (newPage: number) => {
    const [basePath, queryString] = url.split('?');
    const searchParams = new URLSearchParams(queryString || '');

    if (newPage === 1) {
      searchParams.delete('page');
    } else {
      searchParams.set('page', String(newPage));
    }

    const newQueryString = searchParams.toString();
    return newQueryString ? `${basePath}?${newQueryString}` : basePath;
  };

  if (totalPages <= 1) {
    return null;
  }

  const pageNumbers = generatePageNumbers(currentPage, totalPages);
  const pageButtonClassName =
    'border-transparent hover:border-transparent hover:bg-primary hover:text-primary-foreground';
  const inactiveOnlyClassName = 'bg-transparent text-inherit';
  const navButtonClassName = cn(
    pageButtonClassName,
    inactiveOnlyClassName,
    'size-auto h-9 min-w-9 gap-2 px-3',
  );

  return (
    <Pagination
      aria-label="Pagination"
      className={cn(
        'mt-8 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-4',
        fullWidth ? 'w-full' : 'container',
        isPageEditor && 'pointer-events-none',
        className,
      )}
    >
      {currentPage > 1 && !isPageEditor ? (
        <PaginationButton
          as={Link}
          href={pageHref(currentPage - 1)}
          aria-label="Go to previous page"
          className={cn(navButtonClassName, 'justify-self-start')}
        >
          <Icon icon={faArrowLeft} className="size-3.5" />
          Previous
        </PaginationButton>
      ) : (
        <PaginationButton
          disabled
          aria-label="Go to previous page"
          className={cn(navButtonClassName, 'justify-self-start')}
        >
          <Icon icon={faArrowLeft} className="size-3.5" />
          Previous
        </PaginationButton>
      )}

      <PaginationContent className="justify-self-center">
        {pageNumbers.map((page) =>
          page === 'ellipsis-start' || page === 'ellipsis-end' ? (
            <PaginationItem key={page}>
              <PaginationEllipsis className="font-medium text-inherit opacity-70" />
            </PaginationItem>
          ) : (
            <PaginationItem key={page}>
              {page === currentPage || isPageEditor ? (
                <PaginationButton
                  isActive={page === currentPage}
                  disabled={isPageEditor && page !== currentPage}
                  aria-label={`Go to page ${page}`}
                  aria-current={page === currentPage ? 'page' : undefined}
                  className={cn(pageButtonClassName, page !== currentPage && inactiveOnlyClassName)}
                >
                  {page}
                </PaginationButton>
              ) : (
                <PaginationButton
                  as={Link}
                  href={pageHref(page)}
                  aria-label={`Go to page ${page}`}
                  className={cn(pageButtonClassName, inactiveOnlyClassName)}
                >
                  {page}
                </PaginationButton>
              )}
            </PaginationItem>
          ),
        )}
      </PaginationContent>

      {currentPage < totalPages && !isPageEditor ? (
        <PaginationButton
          as={Link}
          href={pageHref(currentPage + 1)}
          aria-label="Go to next page"
          className={cn(navButtonClassName, 'justify-self-end')}
        >
          Next
          <Icon icon={faArrowRight} className="size-3.5" />
        </PaginationButton>
      ) : (
        <PaginationButton
          disabled
          aria-label="Go to next page"
          className={cn(navButtonClassName, 'justify-self-end')}
        >
          Next
          <Icon icon={faArrowRight} className="size-3.5" />
        </PaginationButton>
      )}
    </Pagination>
  );
}

type PageItem = number | 'ellipsis-start' | 'ellipsis-end';

function generatePageNumbers(currentPage: number, totalPages: number): PageItem[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: PageItem[] = [];

  pages.push(1);

  if (currentPage > 3) {
    pages.push('ellipsis-start');
  } else if (currentPage === 3) {
    pages.push(2);
  }

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let i = start; i <= end; i++) {
    if (!pages.includes(i)) {
      pages.push(i);
    }
  }

  if (currentPage < totalPages - 2) {
    pages.push('ellipsis-end');
  } else if (currentPage === totalPages - 2) {
    pages.push(totalPages - 1);
  }

  if (!pages.includes(totalPages)) {
    pages.push(totalPages);
  }

  return pages;
}
